import React from 'react'
import { observer } from 'mobx-react'
import styles from './DocumentDetailsModal.sass'
import {
  DocumentDetailsModalViewModel,
  Tabs,
} from './DocumentDetailsModalViewModel'
import {
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheet,
  ModalSheetHeaderCloseButton,
  ModalSheetContentBlock,
  ModalSheetContentTitle,
  ModalSheetContentDescription,
  ModalSheetHeaderShell,
} from 'components/ModalSheet'
import Button from 'components/Button'
import FileTypeIcon from 'components/FileTypeIcon'
import { IconType } from 'utils/icon-util'
import { TabBar, TabItem } from 'components/TabBar'
import { match } from 'utils/pattern-match-util'
import ConfirmSheet from 'components/ConfirmSheet'
import DocumentDetailsTab from './DocumentDetailsTab'
import { DialogIllustration } from 'components/Dialog'

interface Props {
  vm: DocumentDetailsModalViewModel
}

export default observer(function DocumentDetailsModal({ vm }: Props) {
  const icon = IconType(vm.document.fileType)

  const pendingTask =
    vm.save.pending || vm.delete.pending || vm.fetchLinkedJobs.pending

  return (
    <ModalSheet showing={vm.showing} onRequestClose={vm.close}>
      <ModalSheetHeaderShell size="auto" bordered>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerMedia}>
              <FileTypeIcon
                height={36}
                type={icon.type}
                primaryColor={icon.color}
                strokeColor="#bcbcbe"
              />
            </div>
            <div className={styles.headerInfo}>
              <div className={styles.headerTitle}>
                <span>{vm.headerTitle}</span>
                {!vm.canEdit && (
                  <span className={styles.readOnlyBadge}>Read Only</span>
                )}
              </div>
              {vm.headerTitle !== vm.backingFileName && (
                <div className={styles.headerDescription}>
                  <em>{vm.backingFileName}</em>
                </div>
              )}
              {vm.documentType && (
                <div className={styles.headerDescription}>
                  {vm.documentType.name}
                </div>
              )}
            </div>
          </div>
          <div className={styles.tabContainer}>
            <TabBar align="left">
              <TabItem
                active={vm.tabController.current.tab === Tabs.Details}
                onClick={() => vm.tabController.change(Tabs.Details)}
              >
                Details
              </TabItem>
            </TabBar>
          </div>
        </div>
        <div className={styles.closeBtn}>
          <ModalSheetHeaderCloseButton onClick={vm.close} />
        </div>
      </ModalSheetHeaderShell>
      <ModalSheetContent>
        {match(vm.tabController.current.tab, {
          [Tabs.Details]: () => <DocumentDetailsTab vm={vm} />,
        })}
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterButtonGroup>
          {vm.canEdit && vm.canRemove && (
            <>
              {vm.hasChanges ? (
                <Button
                  disabled={pendingTask}
                  onClick={() => vm.discardChanges()}
                >
                  Discard Changes
                </Button>
              ) : (
                <Button disabled={pendingTask} onClick={() => vm.delete()}>
                  Delete
                </Button>
              )}
            </>
          )}
        </ModalSheetFooterButtonGroup>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          {vm.canDownload && (
            <Button onClick={vm.downloadLatest}>Download</Button>
          )}
          {vm.canEdit && vm.isDetailsTab && (
            <Button type="primary" disabled={!vm.hasChanges} onClick={vm.save}>
              Save
            </Button>
          )}
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
      <ConfirmSheet
        yes="Delete Forever"
        danger
        state={vm.confirmDocumentDelete}
      >
        <ModalSheetContentBlock>
          <DialogIllustration
            src={require('assets/images/img_remove-collaborator.svg')}
            alt="Illustration of a women holding a phone with a crossed-out bubble above the phone."
          />
          <ModalSheetContentTitle>
            Are you sure you want to delete this file?
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            This action cannot be undone. Are you sure that you want to
            permanently delete this document?
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ConfirmSheet>
    </ModalSheet>
  )
})
